import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, RefreshCw, Check, Sparkles, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { getStrandsForGradeSubject, getSpecificsForGradeSubject } from "../lib/curriculum";
import { SKILL_FOCUS_OPTIONS } from "../lib/options";

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/**
 * 1b Stage B — unit-mode Step 1: Strand → Big Idea → Specific Expectations →
 * Transferable Skills. Self-contained so single-lesson / summative mode is
 * untouched. Big-idea candidates come from POST /api/unit/big-ideas (fetched
 * eagerly once grade + subject + >=1 strand are set, with stale-response
 * guarding). suggested_specific_codes is server-computed; the client only
 * pre-checks what it is handed (no matching logic of its own).
 */
export const UnitStep1 = ({
  curriculum, grade, subject,
  selectedStrandCodes, onToggleStrand,
  selectedExpectationCodes, selectedExpectationDetail = [], onToggleExpectation, onPreselectExpectations,
  bigIdea, onBigIdeaChange,
  skills, onToggleSkill,
  focusStore, resolveSkillId,
  unitFocusAreas = {}, onToggleFocusArea,
  isSplitGrade = false, splitGrades = [], lookforGrade = "", onLookforGrade,
  fieldClass,
}) => {
  const strandOptions = useMemo(
    () => (grade && subject ? getStrandsForGradeSubject(curriculum, grade, subject) : []),
    [curriculum, grade, subject]
  );
  const allSpecifics = useMemo(
    () => (grade && subject ? getSpecificsForGradeSubject(curriculum, grade, subject) : []),
    [curriculum, grade, subject]
  );
  const childlessLetters = (curriculum?.childless_strands?.[grade]?.[subject]) || [];

  const strandLetters = selectedStrandCodes;
  const childBearingSelected = strandLetters.filter((l) => !childlessLetters.includes(l));
  const childlessOnly = strandLetters.length > 0 && childBearingSelected.length === 0;

  // Specifics visible = those of the selected strands (strand letter = code[0]).
  const visibleSpecifics = useMemo(
    () => allSpecifics.filter((r) => strandLetters.includes((r.code || "")[0])),
    [allSpecifics, strandLetters]
  );

  // ---- Big-idea fetch (eager + stale-guarded) ----
  const [status, setStatus] = useState("idle"); // idle|loading|done|error|blocked
  const [candidates, setCandidates] = useState([]);
  const [blockedMsg, setBlockedMsg] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(null); // number | "custom" | null
  const seqRef = useRef(0);
  const strandKey = [...strandLetters].sort().join(",");

  const runFetch = () => {
    if (!grade || !subject || strandLetters.length === 0 || childlessOnly) {
      setStatus("idle"); setCandidates([]); return;
    }
    const seq = ++seqRef.current;
    setStatus("loading"); setBlockedMsg("");
    api.post("/unit/big-ideas", { grade, subject, strands: strandLetters })
      .then((r) => {
        if (seq !== seqRef.current) return; // stale — discard
        const d = r.data || {};
        if (d.blocked) {
          setBlockedMsg(d.report?.message || "No big-idea suggestions for this selection.");
          setCandidates([]); setStatus("blocked"); return;
        }
        setCandidates(d.candidates || []); setStatus("done");
      })
      .catch(() => { if (seq === seqRef.current) setStatus("error"); });
  };

  useEffect(() => { runFetch(); // eslint-disable-next-line
  }, [grade, subject, strandKey]);

  // Preselect specifics for a candidate (or strand-wide fallback).
  const preselectFor = (cand) => {
    let list;
    if (cand && (cand.suggested_specific_codes || []).length) {
      list = cand.suggested_specific_codes.map((c) => ({ subject, strand: c[0], code: c }));
    } else {
      // Teacher-written / no suggestions → all specifics of selected strands.
      list = visibleSpecifics.map((r) => ({ subject, strand: r.code[0], code: r.code, ...(r.grade ? { grade: r.grade } : {}) }));
    }
    onPreselectExpectations(list);
  };

  const pickCandidate = (i) => {
    const c = candidates[i];
    setSelectedIdx(i);
    onBigIdeaChange(c.text, c.source_overall_codes || [], c.suggested_specific_codes || [], "candidate");
    preselectFor(c);
  };
  const editSelectedText = (text) => {
    // Editing keeps the current source/suggested (don't recompute pre-check).
    // Provenance marker (recorded intent, never inferred): write-own stays
    // teacher_written; a candidate becomes candidate_edited once the text
    // diverges from the picked candidate, and reverts to candidate if it
    // matches again exactly.
    let source = "teacher_written";
    if (typeof selectedIdx === "number") {
      const original = (candidates[selectedIdx]?.text || "");
      source = text.trim() === original.trim() ? "candidate" : "candidate_edited";
    }
    onBigIdeaChange(text, bigIdea?.sourceCodes || [], bigIdea?.suggestedCodes || [], source);
  };
  const writeOwn = () => {
    setSelectedIdx("custom");
    onBigIdeaChange("", [], [], "teacher_written");
    preselectFor(null);
  };

  // Grade-aware selection check. For combined-grade units a specific
  // expectation is identified by (code, grade) since codes repeat across
  // grades; for single grades grade is undefined on both sides.
  const isSelected = (code, grade) =>
    selectedExpectationDetail.length
      ? selectedExpectationDetail.some((e) => e.code === code && (e.grade || "") === (grade || ""))
      : selectedExpectationCodes.includes(code);

  const labelCls = "text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]";

  return (
    <div className="col-span-full space-y-6" data-testid="unit-step1">
      {/* TRANSFERABLE SKILLS — multi-select. Moved to the TOP of the unit
          Step-1 flow (Step 16 follow-up) so the skill control is the first
          thing a teacher sees and can't be missed below a long expectations
          list. Focus areas + split-grade look-for grade follow it. */}
      <div className="space-y-2" data-testid="unit-skills">
        <div className={labelCls}>Transferable Skills <span className="text-[#4a6984]/40 font-medium normal-case tracking-normal text-[10px]">(pick one or two — required)</span></div>
        <div className="flex flex-wrap gap-2">
          {SKILL_FOCUS_OPTIONS.map((name) => {
            const on = skills.includes(name);
            const atMax = !on && skills.length >= 2;
            return (
              <button
                type="button" key={name}
                data-testid={`unit-skill-option-${slug(name)}`}
                aria-pressed={on}
                disabled={atMax}
                onClick={() => onToggleSkill(name)}
                className={`px-3 py-1.5 rounded-full border text-[13px] transition-colors ${on ? "bg-[#4a6984] border-[#4a6984] text-white font-semibold" : atMax ? "bg-white border-[#4a6984]/10 text-[#4a6984]/30 cursor-not-allowed" : "bg-white border-[#4a6984]/20 text-[#4a6984] hover:border-[#4a6984]"}`}
              >
                {name}
              </button>
            );
          })}
        </div>
        {skills.length === 0
          ? <p className="text-[11px] text-[#c1440e]" data-testid="unit-skill-required-msg">Pick one or two transferable skills to build this unit.</p>
          : skills.length >= 2
            ? <p className="text-[11px] text-[#4a6984]/60">Two skills selected (the maximum). Deselect one to change it.</p>
            : null}
      </div>

      {/* FOCUS AREAS per selected skill — the closed set Atomic Edu assigns from
          and the teacher can adjust per lesson. Required (>=1 per skill). */}
      {skills.length > 0 && focusStore && (
        <div className="space-y-4" data-testid="unit-focus-areas">
          {skills.map((name) => {
            const sid = resolveSkillId ? resolveSkillId(name) : null;
            const lookfors = (sid && focusStore[sid]?.lookfors) || [];
            const picked = (unitFocusAreas[sid] || []);
            if (!sid || lookfors.length === 0) return null;
            return (
              <div key={sid} className="space-y-2" data-testid={`unit-focus-skill-${slug(name)}`}>
                <div className={labelCls}>
                  Focus areas — {name}{" "}
                  <span className="text-[#4a6984]/40 font-medium normal-case tracking-normal text-[10px]">(pick the look-fors this unit can use — at least one)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {lookfors.map((lf) => {
                    const on = picked.includes(lf.number);
                    return (
                      <button
                        type="button" key={lf.number}
                        data-testid={`unit-focus-option-${slug(name)}-${lf.number}`}
                        aria-pressed={on}
                        title={lf.use_when || lf.descriptor || ""}
                        onClick={() => onToggleFocusArea && onToggleFocusArea(sid, lf.number)}
                        className={`px-3 py-1.5 rounded-full border text-[13px] transition-colors ${on ? "bg-[#4a6984] border-[#4a6984] text-white font-semibold" : "bg-white border-[#4a6984]/20 text-[#4a6984] hover:border-[#4a6984]"}`}
                      >
                        {lf.name}
                      </button>
                    );
                  })}
                </div>
                {picked.length === 0 && <p className="text-[11px] text-[#c1440e]">Pick at least one focus area for {name}.</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* SPLIT-GRADE look-for grade — first-class, no silent default. */}
      {isSplitGrade && (
        <div className="space-y-2" data-testid="unit-lookfor-grade">
          <div className={labelCls}>
            Look-for grade{" "}
            <span className="text-[#4a6984]/40 font-medium normal-case tracking-normal text-[10px]">(this unit spans {splitGrades.join("/")} — choose which grade's skill look-fors to use)</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {splitGrades.map((g) => {
              const on = String(lookforGrade) === String(g);
              return (
                <button
                  type="button" key={g}
                  data-testid={`unit-lookfor-grade-option-${g}`}
                  aria-pressed={on}
                  onClick={() => onLookforGrade && onLookforGrade(String(g))}
                  className={`px-3 py-1.5 rounded-full border text-[13px] transition-colors ${on ? "bg-[#4a6984] border-[#4a6984] text-white font-semibold" : "bg-white border-[#4a6984]/20 text-[#4a6984] hover:border-[#4a6984]"}`}
                >
                  Grade {g}
                </button>
              );
            })}
          </div>
          {!lookforGrade && <p className="text-[11px] text-[#c1440e]">Choose the grade the look-fors come from — both grades still appear on every lesson.</p>}
        </div>
      )}

      {/* STRAND — multi-select */}
      {strandOptions.length > 0 && (
        <div className="space-y-2">
          <div className={labelCls}>Strand <span className="text-[#1c2d3d]/70 font-medium normal-case tracking-normal text-[10px]">(pick one or more — required)</span></div>
          <div className="flex flex-wrap gap-2" data-testid="unit-strand-picker">
            {strandOptions.map((s) => {
              const on = strandLetters.includes(s.code);
              return (
                <button
                  type="button" key={s.code}
                  data-testid={`unit-strand-option-${s.code.toLowerCase()}`}
                  aria-pressed={on}
                  onClick={() => onToggleStrand(s.code)}
                  className={`text-left px-3 py-2 rounded-xl border text-sm transition-colors ${on ? "bg-[#3EB489] border-[#3EB489] text-white font-semibold" : "bg-white border-[#4a6984]/20 text-[#4a6984] hover:border-[#3EB489]"}`}
                >
                  {on ? <Check className="inline h-3.5 w-3.5 mr-1 -mt-0.5" /> : null}
                  {s.code} — {s.name}
                </button>
              );
            })}
          </div>
          {childlessOnly && (
            <p data-testid="unit-strand-childless-note" className="text-[13px] leading-snug text-[#4a6984] bg-[#96ded1]/25 border border-[#3EB489]/30 rounded-xl px-3 py-2">
              {(subject || "").toLowerCase().startsWith("math")
                ? "Strand A is the social-emotional learning skills and mathematical processes — Ontario intends these to be woven through the other strands, not taught on their own. Add a strand with content expectations (Number, Algebra, Data, or Spatial Sense) and Strand A will run through it."
                : "This strand has no content expectations to anchor a unit on its own. Add a strand with specific expectations and this one will support it."}
            </p>
          )}
        </div>
      )}

      {/* BIG IDEA */}
      {strandLetters.length > 0 && !childlessOnly && (
        <div className="space-y-2" data-testid="big-idea-field">
          <div className={labelCls}>
            Big Idea <span className="text-[#1c2d3d]/70 font-medium normal-case tracking-normal text-[10px]">(one sentence — required)</span>
          </div>

          {status === "loading" && (
            <div data-testid="big-idea-loading" className="flex items-center gap-2 text-[#4a6984] text-sm bg-white border border-[#4a6984]/15 rounded-xl px-3 py-3">
              <Loader2 className="h-4 w-4 animate-spin text-[#3EB489]" /> Finding big-idea suggestions for your strands…
            </div>
          )}

          {status === "error" && (
            <div data-testid="big-idea-error" className="text-sm bg-red-50 border border-red-200 rounded-xl px-3 py-3 text-red-700">
              <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Couldn't load suggestions.</div>
              <div className="mt-1 flex items-center gap-3">
                <button type="button" data-testid="big-idea-retry" onClick={runFetch} className="inline-flex items-center gap-1 text-[#3EB489] font-semibold"><RefreshCw className="h-3.5 w-3.5" /> Retry</button>
                <button type="button" data-testid="big-idea-write-own" onClick={writeOwn} className="text-[#4a6984] font-semibold underline underline-offset-2">✍️ Write my own instead</button>
              </div>
            </div>
          )}

          {status === "blocked" && (
            <div className="space-y-2">
              <p className="text-[13px] leading-snug text-[#4a6984] bg-[#96ded1]/25 border border-[#3EB489]/30 rounded-xl px-3 py-2">{blockedMsg}</p>
              <button type="button" data-testid="big-idea-write-own" onClick={writeOwn} className="text-sm font-semibold text-[#3EB489] underline underline-offset-2">
                ✍️ Write my own Big Idea
              </button>
            </div>
          )}

          {status === "done" && candidates.length > 0 && (
            <div className="space-y-2" data-testid="big-idea-candidates">
              <p className="text-[11px] text-[#4a6984]/70 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-[#3EB489]" /> Suggestions (Atomic's synthesis — pick, edit, or write your own):</p>
              {candidates.map((c, i) => (
                <button
                  type="button" key={i}
                  data-testid={`big-idea-candidate-${i}`}
                  onClick={() => pickCandidate(i)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl border text-sm leading-snug whitespace-normal transition-colors ${selectedIdx === i ? "bg-[#3EB489]/10 border-[#3EB489]" : "bg-white border-[#4a6984]/15 hover:border-[#3EB489]"}`}
                >
                  <span className="text-[#1c2d3d]">{c.text}</span>
                </button>
              ))}
              <button type="button" data-testid="big-idea-write-own" onClick={writeOwn} className={`text-sm font-semibold ${selectedIdx === "custom" ? "text-[#3EB489]" : "text-[#4a6984]"} underline underline-offset-2`}>
                ✍️ Write my own instead
              </button>
            </div>
          )}

          {/* Editable confirmed sentence — appears once a candidate is picked or write-own chosen */}
          {(selectedIdx !== null || (bigIdea?.text || "").length > 0) && (
            <textarea
              data-testid="big-idea-textarea"
              value={bigIdea?.text || ""}
              onChange={(e) => editSelectedText(e.target.value)}
              rows={2}
              placeholder="Write the enduring understanding students will keep — one sentence."
              className={`${fieldClass} w-full resize-none leading-snug`}
            />
          )}
        </div>
      )}

      {/* SPECIFIC EXPECTATIONS */}
      {visibleSpecifics.length > 0 && (
        <div className="space-y-2" data-testid="unit-specifics">
          <div className={labelCls}>Specific Expectations <span className="text-[#1c2d3d]/70 font-medium normal-case tracking-normal text-[10px]">(pre-checked from your big idea — adjust freely; full list shown)</span></div>
          <div className="max-h-72 overflow-auto rounded-xl border border-[#4a6984]/15 divide-y divide-[#4a6984]/10 bg-white">
            {visibleSpecifics.map((r) => (
              <label key={`${r.code}-${r.gradeNum || ""}`} data-testid={`unit-specific-option-${r.code.toLowerCase()}${r.gradeNum ? `-g${r.gradeNum}` : ""}`} className="flex gap-3 px-3 py-2 cursor-pointer hover:bg-[#f4f5f7]">
                <input
                  type="checkbox"
                  checked={isSelected(r.code, r.grade)}
                  onChange={() => onToggleExpectation(r.code[0], r.code, r.grade)}
                  className="mt-1 accent-[#3EB489] h-4 w-4 shrink-0"
                />
                <span className="flex flex-col gap-0.5 text-left">
                  <span className="text-[13px] font-extrabold text-[#1c2d3d]">
                    {r.topic ? `${r.topic} — ` : ""}{r.code}
                    {r.grade ? (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-[#1c2d3d] text-white align-middle">Gr {r.gradeNum}</span>
                    ) : null}
                  </span>
                  {r.description ? <span className="text-[11px] text-[#4a6984]/80 leading-snug whitespace-normal">{r.description}</span> : null}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitStep1;
